import { useQuery } from "@tanstack/react-query";
import { ArrowRight, FileText } from "lucide-react";
import { Link } from "react-router";

import { getLibraryDocuments } from "@/api/library-documents/library-documents";
import type { ILibraryDocumentListItem } from "@/api/library-documents/library-documents.types";
import { InlineStatusIndicator } from "@/components/shared/InlineStatusIndicator";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardAction,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    Item,
    ItemContent,
    ItemDescription,
    ItemGroup,
    ItemMedia,
    ItemTitle,
} from "@/components/ui/item";
import { useOrganization } from "@/contexts/frontend/organization/useOrganization";

import { RECENT_LIMIT, staggerStyle } from "./recentWidget";
import {
    RecentWidgetEmpty,
    RecentWidgetError,
    RecentWidgetSkeleton,
    RelativeTime,
} from "./recentWidgetParts";

const LIBRARY_DOCUMENTS = "/organizations/document-library/documents";

// The flat documents endpoint emits `textract_status` verbatim — including the
// in-progress values `pending` / `post_processing` that the shared document map
// doesn't cover. Collapse those to `processing` (library parity) and lowercase
// defensively before the exact-key indicator lookup.
function normalizeStatus(status: string | null): string | null {
    if (!status) return null;
    const lower = status.toLowerCase();
    return lower === "pending" || lower === "post_processing"
        ? "processing"
        : lower;
}

// Folder-aware deep-link. KB docs are excluded server-side, so the base is
// always `/documents/...` (no Knowledge Base branch). `?doc=` seeds selection.
function documentHref(doc: ILibraryDocumentListItem): string {
    return doc.library_folder_id != null
        ? `${LIBRARY_DOCUMENTS}/folder/${doc.library_folder_id}?doc=${doc.id}`
        : `${LIBRARY_DOCUMENTS}?doc=${doc.id}`;
}

/**
 * Primary home widget: the 5 most-recently-uploaded contracts across all
 * folders, each linking back into its folder. Sources the FLAT
 * `getLibraryDocuments` (not the hierarchical `getLibraryList`, which would miss
 * every document inside a folder), KB-excluded and `created_at desc` server-side
 * so there is no client re-sort or folder/scope leakage.
 */
export function RecentDocuments() {
    const { organization } = useOrganization();

    const { data, isPending, isError, refetch } = useQuery({
        queryKey: ["frontend-organization-recent-documents", organization.id],
        queryFn: () =>
            getLibraryDocuments(
                1,
                RECENT_LIMIT,
                { sort_by: "created_at", order: "desc" },
                undefined, // folderId — across all folders
                undefined, // organizationId — tenant-scoped automatically
                null, // search
                false // isKnowledgeBase — exclude Knowledge Base reference files
            ),
    });

    const documents = data?.items ?? [];

    return (
        <Card className="h-full">
            <CardHeader>
                <CardTitle>Recent documents</CardTitle>
                <CardDescription>Across your library</CardDescription>
                <CardAction>
                    <Button
                        variant="link"
                        size="sm"
                        className="text-muted-foreground hover:text-primary h-auto gap-1 px-1"
                        render={<Link to={LIBRARY_DOCUMENTS} />}
                    >
                        Document Library
                        <ArrowRight className="size-3.5 transition-transform duration-150 group-hover/button:translate-x-0.5 motion-reduce:transition-none" />
                    </Button>
                </CardAction>
            </CardHeader>
            <CardContent>
                {isPending ? (
                    <RecentWidgetSkeleton noun="documents" />
                ) : isError ? (
                    <RecentWidgetError
                        noun="documents"
                        onRetry={() => refetch()}
                    />
                ) : documents.length === 0 ? (
                    <RecentWidgetEmpty
                        icon={<FileText />}
                        title="No documents yet"
                        description="Upload a contract to the Document Library and it will appear here."
                    />
                ) : (
                    <ItemGroup className="gap-1">
                        {documents.map((document, index) => (
                            <DocumentRow
                                key={document.id}
                                document={document}
                                index={index}
                            />
                        ))}
                    </ItemGroup>
                )}
            </CardContent>
        </Card>
    );
}

function DocumentRow({
    document,
    index,
}: {
    document: ILibraryDocumentListItem;
    index: number;
}) {
    const status = normalizeStatus(document.textract_status);
    const meta = [document.client_name, document.industry_name]
        .filter(Boolean)
        .join(" · ");

    return (
        <Item
            render={<Link to={documentHref(document)} />}
            className="group/row animate-in fade-in-0 slide-in-from-bottom-1 motion-reduce:animate-none"
            style={staggerStyle(index)}
        >
            <ItemMedia
                variant="icon"
                className="bg-muted text-muted-foreground group-hover/row:bg-primary/10 group-hover/row:text-primary relative size-8 self-center rounded-lg transition-colors duration-150 motion-reduce:transition-none"
            >
                <FileText />
                <InlineStatusIndicator
                    variant="dot"
                    type="document"
                    status={status}
                    className="ring-card absolute -right-0.5 -bottom-0.5 ring-2"
                />
            </ItemMedia>
            <ItemContent className="min-w-0 gap-0.5">
                <ItemTitle
                    className="block w-full truncate"
                    title={document.document_name}
                >
                    {document.document_name}
                </ItemTitle>
                {meta ? (
                    <ItemDescription className="line-clamp-1">
                        {meta}
                    </ItemDescription>
                ) : null}
            </ItemContent>
            <RelativeTime iso={document.created_at} />
        </Item>
    );
}
